import type { GameState } from "@/game/types";
import { DIFFICULTIES, TRADE_PRICES } from "@/game/data";
import { formatSats } from "@/game/format";
import { Button } from "@/components/ui/button";
import { Coins, Minus, Plus } from "lucide-react";

interface TradingScreenProps {
  state: GameState;
  onTrade: (item: "food" | "medicine" | "gear", qty: number, unitPrice: number) => void;
  onClose: () => void;
}

const ITEMS: {
  key: "food" | "medicine" | "gear";
  label: string;
  emoji: string;
}[] = [
  { key: "food", label: "Food", emoji: "🥫" },
  { key: "medicine", label: "Med Kit", emoji: "💊" },
  { key: "gear", label: "Gear", emoji: "🛠️" },
];

export function TradingScreen({ state, onTrade, onClose }: TradingScreenProps) {
  const priceMult = DIFFICULTIES[state.difficulty].priceMult;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-pixel text-sm text-primary">Trading Post</h3>
        <div className="flex items-center gap-1 font-terminal text-xl">
          <Coins className="size-4 text-primary" />
          {formatSats(state.inventory.sats)} sats
        </div>
      </div>
      <p className="font-terminal text-lg text-muted-foreground">
        Everything here trades in Bitcoin Cash — instant, low-fee, no middleman.
      </p>

      <div className="space-y-3">
        {ITEMS.map((item) => {
          const price = Math.round(TRADE_PRICES[item.key] * priceMult);
          const owned = state.inventory[item.key];
          return (
            <div
              key={item.key}
              className="flex items-center justify-between rounded-md border bg-card px-3 py-2"
            >
              <div>
                <div className="font-terminal text-xl">
                  {item.emoji} {item.label}
                </div>
                <div className="font-terminal text-base text-muted-foreground">
                  {formatSats(price)} sats each · you have {owned}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="icon-sm"
                  variant="outline"
                  aria-label={`Sell one ${item.label}`}
                  disabled={owned <= 0}
                  onClick={() => onTrade(item.key, -1, Math.round(price * 0.6))}
                >
                  <Minus />
                </Button>
                <Button
                  size="icon-sm"
                  aria-label={`Buy one ${item.label}`}
                  disabled={state.inventory.sats < price}
                  onClick={() => onTrade(item.key, 1, price)}
                >
                  <Plus />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="font-terminal text-sm text-muted-foreground">
        Sell price is 60% of buy price. Choose wisely, pioneer.
      </p>

      <Button onClick={onClose} variant="secondary" className="w-full font-pixel text-[10px]">
        Back to Camp
      </Button>
    </div>
  );
}
